/**
 * When a check-in is waiting for you (T-902).
 *
 * The redesign's Home leads with one check-in as **due now** and the rest quiet
 * with a time beside them. Templates carry no schedule, so the data has to come
 * from somewhere — and it comes from **reminders**, which already model exactly
 * this and already get it right across DST and travel (T-701). Deriving it adds
 * no field, no backup change, and no second concept of "when should this
 * happen".
 *
 * It also gives reminders a visible job on web. ADR-0004 puts delivery on the
 * native app, so today a schedule is authored and then does nothing anyone can
 * see. This is the first thing that pays back the promise that configuring one
 * early is not wasted.
 *
 * **Due-ness is derived, never stored.** A persisted `dueAt` would be a cache
 * with no invalidation and a second source of truth about time — the mistake
 * ADR-0006 exists to prevent. Computing it fresh costs nothing at this scale.
 */

import type { Entry } from '../model/entry.js'
import type { CheckInTemplate } from '../model/template.js'
import type { ReminderSchedule } from '../model/reminder.js'
import { nextOccurrence } from '../model/reminder.js'
import type { Timestamp } from '../model/timestamp.js'

/** How far back to look for the occurrence that would make a check-in due. */
const LOOKBACK_DAYS = 8

export interface DueCheckIn {
  readonly template: CheckInTemplate
  /** Whether it is waiting: an occurrence has passed with nothing logged since. */
  readonly due: boolean
  /**
   * The occurrence this state is about — the one that has passed when due, the
   * next one when quiet. `null` for a template with no enabled reminder.
   */
  readonly at: Timestamp | null
  /** How long it has been waiting, for choosing which single card to promote. */
  readonly overdueMs: number
}

/**
 * The most recent firing at or before `now`, or `null`.
 *
 * Walked forward from a week back rather than backward from now, because
 * `nextOccurrence` only knows how to go forward — and reimplementing it
 * backwards would be a second answer to the question it already answers, which
 * is the whole thing this module refuses to do.
 */
function lastOccurrence(
  schedule: ReminderSchedule,
  now: Timestamp,
  timeZone: string,
): Timestamp | null {
  let cursor: Timestamp = {
    epochMs: now.epochMs - LOOKBACK_DAYS * 86_400_000,
    timeZone: now.timeZone,
  }
  let last: Timestamp | null = null

  // Bounded: a schedule producing instants at or before its own cursor would
  // otherwise spin, and Home is not worth an unresponsive tab.
  for (let guard = 0; guard < 1_000; guard += 1) {
    const next = nextOccurrence(schedule, cursor, timeZone)
    if (next === null || next.epochMs > now.epochMs) {
      return last
    }
    last = next
    cursor = next
  }
  return last
}

/**
 * Whether this check-in is waiting, and when.
 *
 * Due when its most recent occurrence has passed and nothing has been logged
 * against it since. "Since" is measured against the occurrence's own instant,
 * which `nextOccurrence` produced from a **wall clock** — so a reminder set for
 * 08:00 is not satisfied by an entry from 07:00 the same morning, and the
 * answer stays right across a DST change and after a flight.
 */
export function duenessOf(
  template: CheckInTemplate,
  schedule: ReminderSchedule | undefined,
  entries: readonly Entry[],
  now: Timestamp,
  timeZone: string,
): DueCheckIn {
  if (
    schedule === undefined ||
    !schedule.enabled ||
    schedule.templateId !== template.id ||
    template.archived
  ) {
    // No reminder, no claim about when this should happen. Quiet, and showing
    // no time at all rather than inventing one.
    return { template, due: false, at: null, overdueMs: 0 }
  }

  const last = lastOccurrence(schedule, now, timeZone)
  if (last === null) {
    return {
      template,
      due: false,
      at: nextOccurrence(schedule, now, timeZone),
      overdueMs: 0,
    }
  }

  const loggedSince = entries.some(
    (entry) => entry.templateId === template.id && entry.at.epochMs >= last.epochMs,
  )
  if (loggedSince) {
    return {
      template,
      due: false,
      at: nextOccurrence(schedule, now, timeZone),
      overdueMs: 0,
    }
  }

  return { template, due: true, at: last, overdueMs: now.epochMs - last.epochMs }
}

/**
 * Every check-in, ordered for Home, with **at most one** promoted as due.
 *
 * One card, because two things shouting is one thing shouting. The most overdue
 * wins; the rest fall back to quiet and keep their configured order, so the
 * screen does not reshuffle itself every time a reminder passes.
 */
export function dueness(
  templates: readonly CheckInTemplate[],
  schedules: readonly ReminderSchedule[],
  entries: readonly Entry[],
  now: Timestamp,
  timeZone: string,
): readonly DueCheckIn[] {
  const byTemplate = new Map(
    schedules
      .filter((schedule) => schedule.templateId !== undefined)
      .map((schedule) => [schedule.templateId, schedule] as const),
  )

  const all = templates
    .filter((template) => !template.archived)
    .map((template) =>
      duenessOf(template, byTemplate.get(template.id), entries, now, timeZone),
    )

  const promoted = all
    .filter((candidate) => candidate.due)
    .sort((a, b) => b.overdueMs - a.overdueMs)[0]

  const settled = all.map((candidate) =>
    candidate === promoted ? candidate : { ...candidate, due: false },
  )

  // Due first, then configured order. `position` is the user's own arrangement
  // and the only ordering they can predict.
  return [...settled].sort((a, b) => {
    if (a.due !== b.due) {
      return a.due ? -1 : 1
    }
    return a.template.position - b.template.position
  })
}
